import React, { useState, useMemo, useEffect } from 'react';
import { APIProvider, Map, Marker, InfoWindow } from '@vis.gl/react-google-maps';
import { useIntl } from 'react-intl';
import { defaultMapCenter } from '../../../lib/defaultMapCenter';

const MapView = ({
  users,
  onUserClick,
  apiKey,
  mapTypeId = 'roadmap',
  onUserSelect,
  hoveredUserId,
  selectedUserId,
  onUserHover,
  country
}) => {
  const intl = useIntl();
  const [selectedUser, setSelectedUser] = useState(null);
  const [mapError, setMapError] = useState(null);

  // Use provided API key or fall back to environment variable
  const googleMapsApiKey = apiKey || process.env.GOOGLE_MAPS_API_KEY;

  useEffect(() => {
    // Ensure we have a valid API key
    if (!googleMapsApiKey) {
      setMapError("Google Maps API key is not configured. Please provide an API key.");
    }

    // Set up the global document.x object for drawer integration
    if (!document.x) {
      document.x = {};
    }

    // Check if Google Maps API is loaded
    if (typeof window !== 'undefined' && window.google && !window.google.maps) {
      setMapError("Google Maps API failed to load properly. Please try again later.");
    }
  }, [googleMapsApiKey]);

  // Filter out users without valid coordinates
  const validUsers = useMemo(() => {
    if (!users || !Array.isArray(users)) {
      return [];
    }
    
    return users.filter(user => (
      user && 
      user.map_latitude && 
      user.map_longitude && 
      !isNaN(parseFloat(user.map_latitude)) && 
      !isNaN(parseFloat(user.map_longitude))
    ));
  }, [users]);

  // Calculate map center and bounds
  const mapConfig = useMemo(() => {
    if (!validUsers?.length) {
      const resolved = country || (typeof window !== 'undefined' ? window.country : null);
      const fallback = defaultMapCenter(resolved);
      return {
        center: { lat: fallback.lat, lng: fallback.lng },
        zoom: fallback.zoom,
      };
    }

    const lats = validUsers.map(user => parseFloat(user.map_latitude));
    const lngs = validUsers.map(user => parseFloat(user.map_longitude));
    
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    
    return {
      center: {
        lat: (minLat + maxLat) / 2,
        lng: (minLng + maxLng) / 2
      },
      zoom: 4,
      bounds: {
        north: maxLat,
        south: minLat,
        east: maxLng,
        west: minLng
      }
    };
  }, [validUsers, country]);

  const resolveCssVar = (varName, fallback) => {
    if (typeof window === 'undefined') return fallback;
    const value = window.getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
    return value || fallback;
  };

  const pinColors = useMemo(() => ({
    default: resolveCssVar('--company-primary', '#5489B6'),
    hovered: resolveCssVar('--company-accent', '#5B5E5A'),
    selected: resolveCssVar('--company-secondary', '#002949')
  }), []);

  const buildPinIcon = (color, scale = 1) => {
    const size = 32 * scale;
    const svg = `
      <svg width="32" height="32" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2C8.134 2 5 5.134 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.866-3.134-7-7-7z" fill="${color}"/>
        <circle cx="12" cy="9" r="3" fill="#ffffff" />
      </svg>
    `;
    const url = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
    const maps = window?.google?.maps;
    const scaledSize = maps?.Size ? new maps.Size(size, size) : undefined;
    const anchor = maps?.Point ? new maps.Point(size / 2, size) : undefined;
    return { url, scaledSize, anchor };
  };

  const handleMarkerClick = (user) => {
    console.log("MapView: Marker clicked for user", user);
    if (!user || !user.user_name) {
      console.error("MapView: Invalid user data", user);
      return;
    }
    if (onUserSelect) {
      onUserSelect(user);
    }
    // setSelectedUser(user);  // Set selected user to show InfoWindow
    console.log("MapView: Setting document.x.a to", user.fb_user_name || user.user_name);
    document.x.a = user.fb_user_name || user.user_name;  // Trigger drawer opening
  };

  // If there's an error, show error message
  if (mapError) {
    return (
      <div className="flex items-center justify-center h-full w-full bg-gray-100 rounded-lg p-4">
        <div className="text-center p-4 text-red-600">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="font-medium text-lg">{mapError}</p>
        </div>
      </div>
    );
  }

  // If there are no valid users with coordinates, show no results message
  if (validUsers.length === 0) {
    return (
      <div className="flex items-center justify-center h-full w-full bg-gray-100 rounded-lg p-4">
        <div className="text-center text-gray-600">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          <p className="font-medium">{intl.formatMessage({ id: 'succession.search.map_view.no_location_data' })}</p>
          <p className="text-sm mt-2">{intl.formatMessage({ id: 'succession.search.map_view.try_different_search' })}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full rounded-lg overflow-hidden">
      <APIProvider apiKey={googleMapsApiKey}>
        <Map
          defaultCenter={mapConfig.center}
          defaultZoom={mapConfig.zoom}
          defaultBounds={mapConfig.bounds}
          mapTypeId={mapTypeId}
          minZoom={3}
          maxZoom={10}
          gestureHandling={'greedy'}
          disableDefaultUI={true}
          zoomControl={true}
          style={{ width: '100%', height: '100%' }}
        >
          {validUsers.map((user) => (
            <Marker
              key={user.id}
              position={{
                lat: parseFloat(user.map_latitude),
                lng: parseFloat(user.map_longitude),
              }}
              icon={(() => {
                const isSelected = selectedUserId === user.id;
                const isHovered = hoveredUserId === user.id;
                const color = isSelected ? pinColors.selected : isHovered ? pinColors.hovered : pinColors.default;
                const scale = isSelected || isHovered ? 1.25 : 1;
                return buildPinIcon(color, scale);
              })()}
              zIndex={hoveredUserId === user.id ? 1000 : selectedUserId === user.id ? 900 : 1}
              onMouseOver={() => onUserHover?.(user)}
              onMouseOut={() => onUserHover?.(null)}
              onClick={() => handleMarkerClick(user)}
            />
          ))}
          
          {selectedUser && (
            <InfoWindow
              position={{
                lat: parseFloat(selectedUser.map_latitude),
                lng: parseFloat(selectedUser.map_longitude),
              }}
              onCloseClick={() => setSelectedUser(null)}
            >
              <div className="p-3">
                <div className="bg-white rounded-lg shadow-md max-w-sm">
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">
                      {selectedUser.user_name}
                    </h3>
                    {selectedUser.headline && (
                      <p className="text-sm text-gray-600 mb-2">
                        {selectedUser.headline}
                      </p>
                    )}
                    {selectedUser.city && (
                      <p className="text-sm text-gray-500">
                        {`${selectedUser.city}${selectedUser.province ? `, ${selectedUser.province}` : ''}`}
                      </p>
                    )}
                    <div className="mt-3">
                      <button
                        className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onClick={() => document.x.a = selectedUser.fb_user_name || selectedUser.user_name}
                      >
                        {intl.formatMessage({ id: 'succession.search.map_view.view_profile' })}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </InfoWindow>
          )}
        </Map>
      </APIProvider>
    </div>
  );
};

export default MapView; 